/**
 * ADR-0092 — /app/workspace/pdos/new-from-upload
 *
 * The catalog quickcreate flow for upload-source PDOs. Collects name + scope +
 * retention policy upfront, then runs an inline sniff → confirm-mapping →
 * materialize cycle via the single `POST /tenant/operations/upload-pdo-quickcreate`
 * endpoint (creates PDO + single-node wrapper operation + materializes v1
 * atomically). On success navigates to the operation editor with the persist
 * node selected.
 *
 * Quickcreate UX = Option B per spec §10.3 (dedicated route, not modal).
 * Operation name = PDO name verbatim per §10.4 Option A.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { useToast } from "../components/Toast.js";
import { ApiRequestError } from "../lib/api.js";
import { useMyGroups } from "../lib/scope.js";
import { getUploadLimits, uploadSniff, quickcreateUploadPdo } from "../lib/pdoUploadApi.js";
import {
  ANALYZING_LABEL,
  compressingLabel,
  exceedsUploadLimit,
  fileTooLargeMessage,
  uploadProgressLabel,
} from "../lib/pdoUploadTypes.js";
import type {
  ColumnResolutionInput,
  SniffResult,
  SniffColumn,
  UploadParser,
  UploadParserConfig,
  UploadRetentionPolicy,
} from "../lib/pdoUploadTypes.js";
import { ColumnDomainSelect } from "../components/ColumnDomainControl.js";
import { isColumnDomain, type ColumnDomain } from "../lib/columnDomain.js";
import { seedDomain } from "../lib/fileProviderWizard.js";

/**
 * Local row state for the quickcreate wizard — a first upload by construction,
 * so every row is a new column whose type is the sniff's seed unless the
 * person overrides it (ADR-0192 §5.5: the domain vocabulary, never the sniff's).
 */
type WizardColumnRow = {
  source_header: string;
  normalized_header: string;
  inferred_type: string;
  user_resolution: "new" | "skip";
  canonical_name: string | null;
  type_override: ColumnDomain | null;
};

type Scope = "personal" | "team" | "shared";

const ACCEPT_EXTS = ".xlsx,.csv,.tsv,.json,.jsonl,.parquet";

const SNAPSHOT_RETENTION_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 1000, label: "Unlimited" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function detectParser(filename: string): UploadParser | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".xlsx")) return "xlsx";
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".tsv")) return "tsv";
  if (lower.endsWith(".jsonl")) return "jsonl";
  if (lower.endsWith(".json")) return "json";
  if (lower.endsWith(".parquet")) return "parquet";
  return null;
}

/**
 * Suggest a PDO name from the chosen filename. Strip extension, swap `_`/`-`
 * for spaces, collapse repeats, title-case each token. Used to pre-fill the
 * Name field after the user picks a file — they can still edit before Create.
 *
 * Examples:
 *   customer_tiers.xlsx       → "Customer Tiers"
 *   customer-tiers_2026q2.csv → "Customer Tiers 2026q2"
 *   tiers.parquet             → "Tiers"
 */
function suggestPdoNameFromFilename(filename: string): string {
  const stem = filename.replace(/\.[^./\\]+$/, "");
  const spaced = stem.replace(/[_\-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!spaced) return "";
  return spaced
    .split(" ")
    .map((tok) => (tok ? tok[0].toUpperCase() + tok.slice(1) : tok))
    .join(" ");
}

function rowsFromSniff(sniff: SniffResult): WizardColumnRow[] {
  // Quickcreate ALWAYS goes against a fresh PDO (is_first_upload === true),
  // so every row is a new_column with canonical_name = normalized header.
  return sniff.columns.map((c: SniffColumn): WizardColumnRow => ({
    source_header: c.source_header,
    normalized_header: c.normalized_header,
    inferred_type: c.inferred_type,
    user_resolution: "new",
    canonical_name: c.resolution.canonical_name ?? c.normalized_header,
    type_override: null,
  }));
}

function rowsToColumnResolutions(rows: WizardColumnRow[]): ColumnResolutionInput[] {
  return rows.map((r) => ({
    source_header: r.source_header,
    resolution: r.user_resolution,
    canonical_name: r.user_resolution === "skip" ? null : r.canonical_name,
    type_override: r.type_override,
  }));
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function NewUploadPdo() {
  const navigate = useNavigate();
  const toast = useToast();
  const { groups, loading: groupsLoading } = useMyGroups();
  const fileRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  // Scope inherited from the catalog tab the user came from (?tab=mine|teams|shared).
  // When the tab fixes the scope, hide the scope picker entirely so the user
  // isn't asked twice for the same decision. Team-tab still needs to pick which
  // team (unless there's exactly one and we can auto-fill).
  const tabParam = searchParams.get("tab");
  const scopeFromTab: Scope | null =
    tabParam === "mine" ? "personal" :
    tabParam === "teams" ? "team" :
    tabParam === "shared" ? "shared" :
    null;

  // Form state — collected upfront before file sniff.
  const [pdoName, setPdoName] = useState("");
  const [scope, setScope] = useState<Scope>(scopeFromTab ?? "personal");
  const [ownerGroupId, setOwnerGroupId] = useState<string>("");
  const [retentionPolicy, setRetentionPolicy] = useState<UploadRetentionPolicy>("replace");
  const [snapshotMax, setSnapshotMax] = useState<number>(20);

  // Sniff state — populated after file selection.
  const [file, setFile] = useState<File | null>(null);
  const [sniff, setSniff] = useState<SniffResult | null>(null);
  const [rows, setRows] = useState<WizardColumnRow[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The landing zone's bounds are read on mount so the first pick does not wait
  // on a round trip; the value is not held in state because nothing on this
  // page renders a number any more. `getUploadLimits` caches for the session,
  // so the pre-check in `handleFileChosen` resolves from that warm cache.
  useEffect(() => { void getUploadLimits(); }, []);
  const [step, setStep] = useState<"form" | "configure" | "creating">("form");
  /**
   * What the picker says while a file is on its way: compressing, then bytes on
   * the wire, then analyzing. Empty when nothing is moving.
   */
  const [readingLabel, setReadingLabel] = useState<string>("");

  // ── Validation ────────────────────────────────────────────────────────────

  const fieldErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    if (!pdoName.trim()) errs.pdoName = "Required";
    if (scope === "team" && !ownerGroupId) errs.ownerGroupId = "Pick a team";
    if (retentionPolicy === "snapshot" && (!Number.isFinite(snapshotMax) || snapshotMax < 1)) {
      errs.snapshotMax = "Pick a retention count";
    }
    return errs;
  }, [pdoName, scope, ownerGroupId, retentionPolicy, snapshotMax]);

  const formValid = Object.keys(fieldErrors).length === 0;

  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleFileChosen(f: File) {
    setError(null);
    setFile(f);
    const parser = detectParser(f.name);
    if (!parser) {
      setError(`Unsupported file type. Allowed: ${ACCEPT_EXTS.replace(/,/g, " ")}`);
      return;
    }
    // Refuse an over-cap file before a byte moves (the server can only say no
    // after the whole file has crossed the wire).
    const limits = await getUploadLimits();
    if (limits && exceedsUploadLimit(f, limits)) {
      setError(fileTooLargeMessage(f, limits));
      return;
    }
    // Auto-fill the PDO name from the filename, but ONLY if the user hasn't
    // typed anything yet — re-pickings shouldn't clobber an edited name.
    if (!pdoName.trim()) {
      const suggested = suggestPdoNameFromFilename(f.name);
      if (suggested) setPdoName(suggested);
    }
    setBusy(true);
    setReadingLabel(compressingLabel(f));
    try {
      // Quickcreate sniff — no pdoId yet, so this is treated as first upload.
      const result = await uploadSniff({
        file: f,
        onPhase: (phase) => {
          if (phase.kind === "compressing") setReadingLabel(compressingLabel(f));
          else if (phase.kind === "uploading") {
            setReadingLabel(uploadProgressLabel(phase.loadedBytes, phase.totalBytes));
          } else setReadingLabel(ANALYZING_LABEL);
        },
      });
      setSniff(result);
      setRows(rowsFromSniff(result));
      setStep("configure");
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setReadingLabel("");
    }
  }

  function updateRow(idx: number, patch: Partial<WizardColumnRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  async function handleCreate() {
    if (!sniff || !file || !formValid) return;
    // Validate canonical names are non-empty + unique.
    const seen = new Set<string>();
    const kept = rows.filter((r) => r.user_resolution !== "skip");
    for (const r of kept) {
      const name = (r.canonical_name ?? "").trim();
      if (!name) {
        setError(`Column "${r.source_header}" needs a name.`);
        return;
      }
      if (!/^[a-z][a-z0-9_]*$/.test(name)) {
        setError(`Column name "${name}" must be lowercase letters/digits/underscores, starting with a letter.`);
        return;
      }
      if (seen.has(name)) {
        setError(`Duplicate canonical name "${name}". Each column needs a unique name.`);
        return;
      }
      seen.add(name);
    }
    if (kept.length === 0) {
      setError("At least one column must be kept (not skipped).");
      return;
    }
    setError(null);
    setBusy(true);
    setStep("creating");
    try {
      const result = await quickcreateUploadPdo({
        pdoName: pdoName.trim(),
        scope,
        ownerGroupId: scope === "team" ? ownerGroupId : null,
        uploadParser: sniff.parser,
        retentionPolicy,
        snapshotMaxVersions: retentionPolicy === "snapshot" ? snapshotMax : undefined,
        sniffId: sniff.sniff_id,
        originalFilename: file.name,
        parserConfig: sniff.applied_config,
        columnResolutions: rowsToColumnResolutions(rows),
      });
      toast.push({
        kind: "success",
        message: `Created "${pdoName}" · ${result.row_count.toLocaleString()} rows`,
      });
      navigate(`/app/build/operations/${encodeURIComponent(result.operation_id)}?node=${encodeURIComponent(result.node_id)}`);
    } catch (e) {
      setStep("configure");
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const breadcrumb = (
    <span>
      <Link to="/app/workspace/pdos">Persistent Data Objects</Link>
      <span className="muted"> / </span>
      New from upload
    </span>
  );

  return (
    <PageLayout title={breadcrumb}>
      <div style={{ display: "grid", gap: 12 }}>
        {/* Step 1: name + scope + retention */}
        <div className="card" style={{ display: "grid", gap: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Details</div>
          <label className="field">
            <span className="label">Name *</span>
            <input
              className="input"
              value={pdoName}
              onChange={(e) => setPdoName(e.target.value)}
              disabled={busy}
              placeholder="e.g. Customer Tiers"
            />
            {fieldErrors.pdoName && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.pdoName}</div>}
          </label>
          {/* Scope row: hidden when the catalog tab already fixed the scope.
              For team scope we still need to pick a specific team. */}
          {scopeFromTab === null ? (
            <div className="inputRow" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label className="field">
                <span className="label">Scope</span>
                <select
                  className="select"
                  value={scope}
                  onChange={(e) => setScope(e.target.value as Scope)}
                  disabled={busy}
                >
                  <option value="personal">Personal — you and your management chain</option>
                  <option value="team" disabled={groups.length === 0}>
                    Team — members of one team
                  </option>
                  <option value="shared">Shared — everyone in this tenant</option>
                </select>
                <div className="help">
                  {scope === "personal" && "Visible to you and your management chain."}
                  {scope === "team" && "Members of the selected team can read and edit."}
                  {scope === "shared" && "Everyone in this tenant sees this PDO."}
                </div>
              </label>
              {scope === "team" && (
                <label className="field">
                  <span className="label">Team</span>
                  <select
                    className="select"
                    value={ownerGroupId}
                    onChange={(e) => setOwnerGroupId(e.target.value)}
                    disabled={busy || groupsLoading}
                  >
                    <option value="">Pick a team…</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  {fieldErrors.ownerGroupId && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.ownerGroupId}</div>}
                </label>
              )}
            </div>
          ) : scope === "team" ? (
            <label className="field">
              <span className="label">Team</span>
              <select
                className="select"
                value={ownerGroupId}
                onChange={(e) => setOwnerGroupId(e.target.value)}
                disabled={busy || groupsLoading}
              >
                <option value="">Pick a team…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              <div className="help">Members of the selected team can read and edit.</div>
              {fieldErrors.ownerGroupId && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.ownerGroupId}</div>}
            </label>
          ) : null}
          <div className="inputRow" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label className="field">
              <span className="label">Retention</span>
              <select
                className="select"
                value={retentionPolicy}
                onChange={(e) => setRetentionPolicy(e.target.value as UploadRetentionPolicy)}
                disabled={busy}
              >
                <option value="replace">Replace — only the latest upload</option>
                <option value="snapshot">Snapshot — keep history</option>
              </select>
              <div className="help">
                {retentionPolicy === "replace"
                  ? "Schema may change on re-upload (you'll confirm)."
                  : "Schema must match across versions. Pure renames are allowed."}
              </div>
            </label>
            {retentionPolicy === "snapshot" && (
              <label className="field">
                <span className="label">Keep last</span>
                <select
                  className="select"
                  value={snapshotMax}
                  onChange={(e) => setSnapshotMax(parseInt(e.target.value, 10))}
                  disabled={busy}
                >
                  {SNAPSHOT_RETENTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <div className="help">Older versions are pruned beyond this count.</div>
              </label>
            )}
          </div>
        </div>

        {/* Step 2: choose file */}
        {step === "form" && (
          <div className="card" style={{ display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Upload data</div>
            {/*
              No size is stated. The per-file cap retired with the tenant-scoped
              landing zone, and the workspace budget that replaced it is not a
              limit on the file anyone is picking — quoting it here would read as
              one. An upload that genuinely cannot land is refused below, naming
              the actual bound. The served bounds are still read for that check.
            */}
            <div className="help">
              Accepted: .xlsx .csv .tsv .json .jsonl .parquet
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT_EXTS}
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFileChosen(f);
                e.target.value = "";
              }}
            />
            <div className="actions">
              <button
                className="btn btnPrimary"
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                {busy ? "Reading file…" : "Choose file"}
              </button>
              <Link className="btn" to="/app/workspace/pdos">Cancel</Link>
            </div>
            {busy && readingLabel ? (
              <div className="muted" style={{ fontSize: 13 }}>{readingLabel}</div>
            ) : null}
            <div className="help">
              We&rsquo;ll suggest a name from the filename — you can edit it before saving.
            </div>
            {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          </div>
        )}

        {/* Step 3: confirm parsing + canonical names + Create */}
        {step === "configure" && sniff && file && (
          <div className="card" style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Confirm columns</div>
            <div className="muted">{file.name} · {sniff.row_count.toLocaleString()} rows</div>

            {sniff.duplicate_source_headers.length > 0 && (
              <div className="help" style={{ color: "var(--danger-text)" }}>
                ✕ Duplicate headers in upload:{" "}
                {sniff.duplicate_source_headers
                  .map((d) => `"${d.normalized}" (positions ${d.positions.join(",")})`)
                  .join("; ")}
                . Each column must have a unique header.
              </div>
            )}


            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Source header</th>
                    <th>Canonical name</th>
                    <th>Type</th>
                    <th>Keep</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx}>
                      <td><code className="mono">{r.source_header}</code></td>
                      <td>
                        <input
                          className="input"
                          value={r.canonical_name ?? ""}
                          placeholder={r.normalized_header}
                          disabled={busy || r.user_resolution === "skip"}
                          onChange={(e) => updateRow(idx, { canonical_name: e.target.value })}
                          style={{ minWidth: 180 }}
                        />
                      </td>
                      <td>
                        {/* ADR-0192 §5.5 — the one per-upload type control that survives:
                            the first upload's seed, in the domain vocabulary. Auto = the
                            sniff's proposal; an explicit choice is sent as type_override. */}
                        <ColumnDomainSelect
                          columnName={r.canonical_name ?? r.normalized_header}
                          resolvedDomain={seedDomain(r.inferred_type)}
                          override={r.type_override ?? undefined}
                          disabled={busy || r.user_resolution === "skip"}
                          onChange={(_col, domain) => updateRow(idx, { type_override: isColumnDomain(domain) ? domain : null })}
                        />
                      </td>
                      <td>
                        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={r.user_resolution !== "skip"}
                            disabled={busy}
                            onChange={(e) => updateRow(idx, { user_resolution: e.target.checked ? "new" : "skip" })}
                          />
                          <span className="muted">{r.user_resolution === "skip" ? "skip" : "keep"}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="actions">
              <button
                className="btn btnPrimary"
                type="button"
                disabled={busy || !formValid || sniff.duplicate_source_headers.length > 0}
                onClick={() => void handleCreate()}
              >
                {busy ? "Creating…" : "Create PDO"}
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setStep("form");
                  setFile(null);
                  setSniff(null);
                  setRows([]);
                  setError(null);
                }}
                disabled={busy}
              >
                Pick a different file
              </button>
              <Link className="btn" to="/app/workspace/pdos" aria-disabled={busy ? true : undefined}>Cancel</Link>
            </div>

            {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          </div>
        )}

        {step === "creating" && (
          <div className="card">
            <div className="muted">Creating PDO and uploading v1…</div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

// Unused-import guard for TS strict — silences lint if a parser type imports
// turn out to be unused after build. Safe no-op.
type _Unused = UploadParserConfig;
